import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Webhook, Copy, Check, AlertCircle, ShieldCheck, Code2, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton, CardSkeleton } from "@/components/SkeletonLoader";

export default function IntegrationsPage() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/intake/_/info");
        setInfo(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore caricamento configurazione");
      } finally { setLoading(false); }
    })();
  }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`${label} copiato`);
      setTimeout(() => setCopied(""), 2000);
    } catch { toast.error("Copia non riuscita"); }
  };

  if (loading) {
    return <AppLayout title="Integrazioni"><div className="flex items-center justify-center py-20 text-slate-500 mr-2"><Loader2 className="w-6 h-6 animate-spin" />Caricamento…</div></AppLayout>;
  }
  if (!info) {
    return <AppLayout title="Integrazioni"><div className="text-rose-700">Configurazione non disponibile.</div></AppLayout>;
  }

  const curl = `curl -X POST "${info.endpoint}" \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: ${info.headers['X-Api-Key']}" \\
  -d '${JSON.stringify(info.payload_example, null, 2)}'`;

  const lovableSnippet = `// Esempio: Supabase Edge Function (Lovable) — invia segnalazione al CRM
// File: supabase/functions/notify-crm/index.ts

const CRM_ENDPOINT = "${info.endpoint}";
const CRM_API_KEY = "${info.headers['X-Api-Key']}";

Deno.serve(async (req) => {
  const { record } = await req.json(); // record dalla tua tabella segnalazioni

  const r = await fetch(CRM_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Api-Key": CRM_API_KEY,
    },
    body: JSON.stringify({
      nome_completo: record.nome,             // es. "SALVATORE BERARDO IACOBELLI"
      telefono: record.telefono,
      mail: record.mail || "",
      importo_richiesto: String(record.importo_richiesto), // "5000" o "5.000,00 €"
      segnalatore: record.segnalatore_email,
      note: record.note,
      source_id: record.id,                  // id univoco lato Lovable: evita duplicati
    }),
  });

  const data = await r.json();
  return new Response(JSON.stringify(data), {
    headers: { "Content-Type": "application/json" },
  });
});`;

  return (
    <AppLayout
      title="Integrazioni & Webhook"
      subtitle="Connetti app esterne al CRM"
    >
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0A192F] to-[#172A45] text-white border border-[#23375a] rounded-[3px] p-6 mb-5" data-testid="integrations-hero">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-[3px] bg-[#172A45] border border-[#23375a] flex items-center justify-center text-[#B7955B] shrink-0">
            <Webhook className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#B7955B]">Lead intake webhook</div>
            <h2 className="font-serif text-2xl mt-1">Ricevi segnalazioni in tempo reale</h2>
            <p className="text-slate-300 text-sm mt-2 max-w-3xl leading-relaxed">
              Configura una qualsiasi app esterna (Lovable, Zapier, Make, n8n, una Edge Function ecc.) per inviare
              automaticamente le segnalazioni al CRM. Ogni segnalazione diventa un <strong className="text-[#B7955B]">lead caldo</strong>:
              cliente in stato <em>INTERESSATO</em>, recall di oggi (compare in Daily), badge <em>SEGNAL</em> visibile in lista.
            </p>
          </div>
        </div>
      </div>

      {/* Provider WhatsApp */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <a href="/integrazioni/360dialog" data-testid="link-d360"
           className="bg-white border border-emerald-200 hover:border-emerald-400 rounded-[3px] p-5 group transition-all shadow-[0_1px_3px_rgba(10,25,47,0.05)] hover:shadow-md">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-[3px] bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-700 shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Ufficiale · Cold outreach</div>
              <div className="font-serif text-lg text-slate-900">WhatsApp via 360dialog</div>
              <p className="text-xs text-slate-600 mt-1">
                Meta Cloud API ufficiale. Template pre-approvati, zero rischio ban. Da <b>€49/mese</b> + costo variabile Meta.
              </p>
              <div className="mt-2 text-xs text-[#B7955B] font-medium group-hover:underline">Configura →</div>
            </div>
          </div>
        </a>

        <a href="/integrazioni/whatsapp" data-testid="link-waha"
           className="bg-white border border-slate-200 hover:border-slate-400 rounded-[3px] p-5 group transition-all shadow-[0_1px_3px_rgba(10,25,47,0.05)] hover:shadow-md">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-[3px] bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 shrink-0">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Legacy · WhatsApp Web</div>
              <div className="font-serif text-lg text-slate-900">WhatsApp via WAHA (QR code)</div>
              <p className="text-xs text-slate-600 mt-1">
                Bridge non-ufficiale. Rischio ban <b>elevato per cold outreach</b>. Utile solo per numeri già in contatto.
              </p>
              <div className="mt-2 text-xs text-slate-500 group-hover:text-slate-900">Configura →</div>
            </div>
          </div>
        </a>
      </div>

      {/* Config */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <Card title="Endpoint" icon={Link2}>
          <CodeRow label="URL" value={info.endpoint} onCopy={() => copy(info.endpoint, "URL")} copied={copied === "URL"} />
          <CodeRow label="Metodo" value={info.method} />
          <CodeRow label="Content-Type" value={info.headers["Content-Type"]} />
        </Card>

        <Card title="Autenticazione" icon={ShieldCheck} accent>
          <div className="text-xs text-slate-500 mb-2">Aggiungi questo header ad ogni richiesta:</div>
          <CodeRow label="X-Api-Key" value={info.headers["X-Api-Key"]} mono onCopy={() => copy(info.headers["X-Api-Key"], "API Key")} copied={copied === "API Key"} />
          <div className="mt-3 flex items-start gap-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-[3px] p-2">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Trattala come una password. Chi la possiede può creare lead nel tuo CRM. Se compromessa, modificala in <code className="bg-white px-1 rounded">/app/backend/.env</code>.</span>
          </div>
        </Card>
      </div>

      {/* Payload */}
      <Card title="Payload (JSON)" icon={Code2}>
        <div className="text-xs text-slate-500 mb-2">Formato del body. I campi sono tutti opzionali tranne il nome (nome / cognome o nome_completo) e il telefono.</div>
        <pre className="bg-slate-900 text-slate-100 text-xs rounded-[3px] p-4 overflow-x-auto font-mono leading-relaxed">{JSON.stringify(info.payload_example, null, 2)}</pre>
        <button onClick={() => copy(JSON.stringify(info.payload_example, null, 2), "Payload")} className="mt-2 text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1">
          {copied === "Payload" ? <Check className="text-emerald-600 w-3 h-3" /> : <Copy className="w-3 h-3" />}
          Copia payload di esempio
        </button>
      </Card>

      {/* Test cURL */}
      <Card title="Test rapido (cURL)" icon={Code2} className="mt-4">
        <div className="text-xs text-slate-500 mb-2">Incolla nel terminale per testare subito:</div>
        <pre className="bg-slate-900 text-slate-100 text-xs rounded-[3px] p-4 overflow-x-auto font-mono leading-relaxed whitespace-pre">{curl}</pre>
        <button onClick={() => copy(curl, "cURL")} className="mt-2 text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1">
          {copied === "cURL" ? <Check className="text-emerald-600 w-3 h-3" /> : <Copy className="w-3 h-3" />}
          Copia comando
        </button>
      </Card>

      {/* Lovable snippet */}
      <Card title="Snippet pronto per Lovable / Supabase Edge Function" icon={Code2} className="mt-4">
        <div className="text-xs text-slate-500 mb-2 bg-slate-100 px-1">Copia in <code >supabase/functions/notify-crm/index.ts</code> e collega via trigger sulla tabella segnalazioni.</div>
        <pre className="bg-slate-900 text-slate-100 text-xs rounded-[3px] p-4 overflow-x-auto font-mono leading-relaxed whitespace-pre">{lovableSnippet}</pre>
        <button onClick={() => copy(lovableSnippet, "Snippet Lovable")} className="mt-2 text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1">
          {copied === "Snippet Lovable" ? <Check className="text-emerald-600 w-3 h-3" /> : <Copy className="w-3 h-3" />}
          Copia snippet
        </button>
      </Card>

      {/* Notes */}
      <Card title="Note operative" icon={AlertCircle} className="mt-4">
        <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">
          {(info.notes || []).map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      </Card>

      {/* Response */}
      <Card title="Risposta attesa" icon={Code2} className="mt-4">
        <pre className="bg-slate-50 border border-slate-200 text-xs rounded-[3px] p-4 overflow-x-auto font-mono leading-relaxed">{JSON.stringify(info.response_example, null, 2)}</pre>
      </Card>
    </AppLayout>
  );
}

function Card({ title, icon: Icon, accent, className = "", children }) {
  return (
    <div className={`bg-white border ${accent ? "border-[#B7955B]" : "border-slate-200"} rounded-[3px] shadow-[0_1px_3px_rgba(10,25,47,0.05)] ${className}`}>
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[#F0F2F5]">
        {Icon && <Icon className={`w-4 h-4 ${accent ? "text-[#B7955B]" : "text-slate-600"}`} />}
        <h3 className="font-serif text-base text-slate-900 font-medium">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function CodeRow({ label, value, mono = false, onCopy, copied }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-[#F0F2F5] last:border-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 w-28 shrink-0">{label}</div>
      <code className={`flex-1 text-sm bg-slate-50 px-3 py-2 rounded-[3px] border border-slate-200 break-all ${mono ? "font-mono text-[#8a6e3d] font-semibold" : "text-slate-900"}`}>{value}</code>
      {onCopy && (
        <button onClick={onCopy} className="p-2 hover:bg-slate-100 rounded-[3px] shrink-0 text-slate-600">
          {copied ? <Check className="text-emerald-600 w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      )}
    </div>
  );
}
